import { computed, onMounted, reactive, ref, watch } from 'vue'
import usePost from '../../core/composables/api/usePost'
import { useEventBus } from '@vueuse/core'
import { EventKey } from '../../core/events/event-key'
import { useSnackStore } from '../../stores/snack.store'
import { useI18n } from 'vue-i18n'
import { ApiRequestStatus } from '../../core/models/api/api-request-status'
import { useRouter } from 'vue-router'
import { FeedbackTopicType } from '@/core/models/feedback-topic'
import type {
  QuestionGroupSection,
  StructuredFeedbackTemplate,
} from '@/modules/survey/models/structured-feedback-template'
import { useFeedbackTopicStore } from '@/stores/feedback-topic.store'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user.store'
import type { GatheringEntry, GatheringMechanism, Perspective } from '@/core/models/perspective'
import {
  emptyPerspectiveGatheringState,
  SELF_PERSPECTIVE_KEY,
  type PerspectiveGatheringState,
} from '@/core/models/perspective'

export type { PerspectiveGatheringState }

const REQUEST_FEEDBACK_STEPS = {
  selectTemplates: 'select-templates',
  selectPerspectives: 'select-perspectives',
  selectQuestionGroups: 'select-question-groups',
  addMetadata: 'add-metadata',
} as const

type RequestFeedbackStep = (typeof REQUEST_FEEDBACK_STEPS)[keyof typeof REQUEST_FEEDBACK_STEPS]

export const requestFeedback = (callback?: () => void) => {
  const bus = useEventBus(EventKey.surveyUpdated)
  const {
    mutate,
    status: savingStatus,
    data: createdSurveyId,
  } = usePost<string, SurveyFormData>('survey')
  const snackStore = useSnackStore()
  const { t } = useI18n()
  const router = useRouter()
  const userStore = useUserStore()

  const form = reactive<{
    title?: string
    message?: JSON
    includeSelf: boolean
    templateIds: string[]
    questionGroupIds: string[]
    perspectiveGathering: Record<string, PerspectiveGatheringState>
  }>({
    title: undefined,
    message: undefined,
    includeSelf: false,
    templateIds: [],
    questionGroupIds: [],
    perspectiveGathering: {},
  })

  const feedbackTopicStore = useFeedbackTopicStore()
  const { templates, loadingStatus } = storeToRefs(feedbackTopicStore)
  const { loadTemplates } = feedbackTopicStore

  const filteredTemplates = computed(() =>
    templates.value.filter(
      (template) =>
        template.type === FeedbackTopicType.Evaluation || template.type === FeedbackTopicType.Pulse,
    ),
  )

  const selectedTemplates = computed(() =>
    filteredTemplates.value.filter((template) => form.templateIds.includes(template.id)),
  )

  const includeSelfAvailable = computed(() => {
    if (selectedTemplates.value.length === 0) return false
    return selectedTemplates.value.every((template) => template.includeSelf)
  })

  const selectedMemberIds = computed(() => {
    return [
      ...new Set(
        Object.values(form.perspectiveGathering).flatMap((state) => state.appUserMemberIds),
      ),
    ]
  })

  const availablePerspectives = computed((): Perspective[] => {
    if (selectedTemplates.value.length === 0) return []

    const [first, ...rest] = selectedTemplates.value
    let intersected: Perspective[] = first.perspectives.map((p) => ({
      ...p,
      allowedMechanisms: [...p.allowedMechanisms],
    }))

    for (const template of rest) {
      intersected = intersected
        .filter((p) => template.perspectives.some((tp) => tp.key === p.key))
        .map((p) => {
          const other = template.perspectives.find((tp) => tp.key === p.key)!
          const mechanisms = p.allowedMechanisms.filter((m) =>
            other.allowedMechanisms.includes(m),
          ) as GatheringMechanism[]
          return { ...p, allowedMechanisms: mechanisms }
        })
        .filter((p) => p.allowedMechanisms.length > 0)
    }

    return intersected
  })

  const selfRequired = computed(() =>
    selectedTemplates.value.some(
      (template) => template.includeSelf && template.perspectives.length === 0,
    ),
  )

  const requiredPerspectiveKeys = computed(() => {
    const keys = new Set<string>()
    for (const template of selectedTemplates.value) {
      if (!template.includeSelf && template.perspectives.length === 1) {
        keys.add(template.perspectives[0].key)
      }
    }
    return keys
  })

  const ensurePerspectiveState = (key: string) => {
    if (!form.perspectiveGathering[key]) {
      form.perspectiveGathering[key] = emptyPerspectiveGatheringState()
    }
    return form.perspectiveGathering[key]
  }

  watch(
    availablePerspectives,
    (perspectives) => {
      const keys = new Set(perspectives.map((p) => p.key))
      for (const key of Object.keys(form.perspectiveGathering)) {
        if (!keys.has(key)) {
          delete form.perspectiveGathering[key]
        }
      }
      for (const perspective of perspectives) {
        const state = ensurePerspectiveState(perspective.key)
        if (requiredPerspectiveKeys.value.has(perspective.key)) {
          state.enabled = true
        }
        if (
          perspective.allowedMechanisms.length === 1 &&
          perspective.allowedMechanisms[0] === 'link'
        ) {
          state.useOpenLink = true
        }
      }
    },
    { immediate: true },
  )

  watch(selfRequired, (required) => {
    if (required) {
      form.includeSelf = true
    }
  })

  watch(includeSelfAvailable, (available) => {
    if (!available) {
      form.includeSelf = false
    }
  })

  const buildGathering = (): GatheringEntry[] => {
    const gathering: GatheringEntry[] = []

    if (form.includeSelf && includeSelfAvailable.value) {
      gathering.push({
        perspectiveKey: SELF_PERSPECTIVE_KEY,
        mechanism: 'app_user',
        memberIds: [userStore.user.id],
        enabled: true,
      })
    }

    for (const perspective of availablePerspectives.value) {
      const state = form.perspectiveGathering[perspective.key]
      if (!state?.enabled) continue

      if (perspective.allowedMechanisms.includes('app_user')) {
        gathering.push({
          perspectiveKey: perspective.key,
          mechanism: 'app_user',
          memberIds: [...state.appUserMemberIds],
          enabled: true,
        })
      }
      if (state.useOpenLink && perspective.allowedMechanisms.includes('link')) {
        gathering.push({
          perspectiveKey: perspective.key,
          mechanism: 'link',
          enabled: true,
        })
      }
    }

    return gathering
  }

  const skipTemplateSelection = computed(() => filteredTemplates.value.length === 1)

  const autoSelectSingleTemplate = () => {
    if (skipTemplateSelection.value && form.templateIds.length === 0) {
      form.templateIds = [filteredTemplates.value[0].id]
    }
  }

  watch(filteredTemplates, autoSelectSingleTemplate)

  onMounted(() => {
    loadTemplates()
  })

  const errors = ref<ErrorMap>(new Map())

  const validatePerspectiveGathering = () => {
    const memberIdToPerspectiveKeys = new Map<string, string[]>()

    for (const perspective of availablePerspectives.value) {
      const state = form.perspectiveGathering[perspective.key]
      if (!state?.enabled) continue

      const hasAppUser =
        perspective.allowedMechanisms.includes('app_user') && state.appUserMemberIds.length > 0
      const hasOpenLink = state.useOpenLink && perspective.allowedMechanisms.includes('link')

      if (!hasAppUser && !hasOpenLink) {
        errors.value.set(perspective.key, t('feedback.formValidation.members'))
      }

      if (perspective.allowedMechanisms.includes('app_user')) {
        for (const memberId of new Set(state.appUserMemberIds)) {
          const keys = memberIdToPerspectiveKeys.get(memberId) ?? []
          keys.push(perspective.key)
          memberIdToPerspectiveKeys.set(memberId, keys)
        }
      }
    }

    const duplicatePerspectiveKeys = new Set<string>()
    for (const keys of memberIdToPerspectiveKeys.values()) {
      if (keys.length > 1) {
        for (const key of keys) {
          duplicatePerspectiveKeys.add(key)
        }
      }
    }

    for (const key of duplicatePerspectiveKeys) {
      errors.value.set(key, t('feedback.formValidation.duplicateMembers'))
    }
  }

  const validateStep = () => {
    errors.value.clear()

    switch (step.value) {
      case REQUEST_FEEDBACK_STEPS.selectTemplates:
        if (form.templateIds.length === 0) {
          errors.value.set('templateIds', t('feedback.formValidation.templates'))
        }
        break
      case REQUEST_FEEDBACK_STEPS.selectQuestionGroups:
        if (
          form.questionGroupIds.length === 0 &&
          !configurableTemplates.value.some((template) =>
            template.questionGroupSections?.some((section) => section.required),
          )
        ) {
          errors.value.set('questionGroupIds', t('feedback.formValidation.questionGroups'))
        }
        break
      case REQUEST_FEEDBACK_STEPS.selectPerspectives: {
        const gathering = buildGathering()
        const hasNonSelfPerspective = gathering.some(
          (entry) => entry.perspectiveKey !== SELF_PERSPECTIVE_KEY,
        )
        if (!hasNonSelfPerspective) {
          errors.value.set('perspectives', t('startSurvey.gatheringRequired'))
          break
        }
        validatePerspectiveGathering()

        for (const template of selectedTemplates.value) {
          const hasSelf = form.includeSelf && template.includeSelf
          const hasPerspective = template.perspectives.some((p) => {
            const state = form.perspectiveGathering[p.key]
            return state?.enabled
          })
          if (!hasSelf && !hasPerspective) {
            errors.value.set(
              'perspectives',
              t('startSurvey.perspectivesRequired', { templateTitle: template.title }),
            )
          }
        }
        break
      }
    }
  }

  watch(form, () => {
    errors.value.clear()
  })

  const saveSurvey = async () => {
    validateStep()
    if (errors.value.size == 0) {
      await mutate({
        title: form.title,
        message: form.message,
        templateIds: form.templateIds,
        questionGroupIds: form.questionGroupIds,
        gathering: buildGathering(),
      })
    }

    switch (savingStatus.value) {
      case ApiRequestStatus.Success:
        bus.emit()

        if (callback) {
          callback()
        }
        router.push({ name: 'survey', params: { surveyId: createdSurveyId.value } })
        snackStore.showInfo(t('feedback.requestFeedbackSuccess'))

        break
      case ApiRequestStatus.Error:
        snackStore.showError(t('feedback.requestFeedbackErrors'))
        break
    }
  }

  const isConfigurable = computed(() => {
    return configurableTemplates.value.length > 0
  })

  const templateHasSelectableQuestionGroups = (template: StructuredFeedbackTemplate) =>
    !!template.questionGroupSections?.some((section) => !section.required)

  const configurableTemplates = computed(() => {
    return templates.value
      .filter((template) => form.templateIds.includes(template.id))
      .filter((template) => templateHasSelectableQuestionGroups(template))
  })

  const getTemplateQuestionGroupIds = (
    templateId: string,
    sectionFilter: (section: QuestionGroupSection) => boolean = () => true,
  ) => {
    const sections =
      templates.value.find((template) => template.id === templateId)?.questionGroupSections ?? []

    return sections
      .filter(sectionFilter)
      .flatMap((section) => section.questionGroups)
      .map((questionGroup) => questionGroup.id)
  }

  watch(
    () => [...form.templateIds],
    (templateIds, previousTemplateIds = []) => {
      const addedTemplateIds = templateIds.filter((id) => !previousTemplateIds.includes(id))
      const removedTemplateIds = previousTemplateIds.filter((id) => !templateIds.includes(id))

      const addedQuestionGroupIds = addedTemplateIds.flatMap((templateId) => {
        const template = templates.value.find((item) => item.id === templateId)
        if (template && templateHasSelectableQuestionGroups(template)) {
          return getTemplateQuestionGroupIds(templateId, (section) => section.required)
        }
        return getTemplateQuestionGroupIds(templateId)
      })
      form.questionGroupIds = Array.from(
        new Set([...form.questionGroupIds, ...addedQuestionGroupIds]),
      )

      const removedQuestionGroupIds = new Set(
        removedTemplateIds.flatMap((templateId) => getTemplateQuestionGroupIds(templateId)),
      )
      form.questionGroupIds = form.questionGroupIds.filter((id) => !removedQuestionGroupIds.has(id))
    },
  )

  const steps = computed<RequestFeedbackStep[]>(() => [
    ...(skipTemplateSelection.value ? [] : [REQUEST_FEEDBACK_STEPS.selectTemplates]),
    ...(isConfigurable.value ? [REQUEST_FEEDBACK_STEPS.selectQuestionGroups] : []),
    REQUEST_FEEDBACK_STEPS.selectPerspectives,
    REQUEST_FEEDBACK_STEPS.addMetadata,
  ])

  const stepIndex = ref(0)

  const step = computed(() => steps.value[stepIndex.value])

  const isFirstStep = computed(() => stepIndex.value === 0)

  const isLastStep = computed(() => stepIndex.value === steps.value.length - 1)

  watch(steps, (newSteps) => {
    if (stepIndex.value >= newSteps.length) {
      stepIndex.value = Math.max(0, newSteps.length - 1)
    }
  })

  const nextStep = () => {
    validateStep()
    if (errors.value.size === 0 && !isLastStep.value) {
      stepIndex.value++
    }
  }

  const previousStep = () => {
    if (stepIndex.value > 0) {
      stepIndex.value--
      errors.value.clear()
    }
  }

  const clearForm = () => {
    errors.value.clear()
    form.title = undefined
    form.message = undefined
    form.includeSelf = false
    form.templateIds = []
    form.questionGroupIds = []
    form.perspectiveGathering = {}
    stepIndex.value = 0
  }

  return {
    nextStep,
    previousStep,
    step,
    isFirstStep,
    isLastStep,
    saveSurvey,
    form,
    errors,
    templates: filteredTemplates,
    clearForm,
    loadingStatus,
    availablePerspectives,
    includeSelfAvailable,
    selfRequired,
    requiredPerspectiveKeys,
    configurableTemplates,
    autoSelectSingleTemplate,
    selectedMemberIds,
    ensurePerspectiveState,
  }
}

type SurveyFormData = {
  title?: string
  message?: JSON
  templateIds: string[]
  questionGroupIds: string[]
  gathering: GatheringEntry[]
}

type ErrorMap = Map<string, string>
